import { useYoutubeChannelConnect } from "@/lib/queries/channel";
import { Avatar, AvatarFallback, AvatarImage } from "@repo/ui";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@repo/ui";
import { CheckCircle, Loader2, Youtube } from "lucide-react";
import { useState } from "react";

interface YouTubeChannel {
  platformAccountId: string;
  displayName: string;
  handle?: string;
  avatarUrl?: string;
  profileUrl?: string;
}

interface YouTubeChannelPickerProps {
  channels: YouTubeChannel[];
  tokenBundle: string;
  organizationId: string;
  profileId: string;
  onSuccess: () => void;
  onError: (message: string) => void;
}

export function YouTubeChannelPicker({
  channels,
  tokenBundle,
  organizationId,
  profileId,
  onSuccess,
  onError,
}: YouTubeChannelPickerProps) {
  const [selectedId, setSelectedId] = useState<string>(
    channels[0]?.platformAccountId ?? "",
  );

  const connectMutation = useYoutubeChannelConnect(organizationId, profileId);

  const handleConnect = () => {
    if (!selectedId) return;
    connectMutation.mutate(
      { organizationId, tokenBundle, channelId: selectedId },
      {
        onSuccess: () => onSuccess(),
        onError: (err) =>
          onError(
            err instanceof Error ? err.message : "Failed to connect channel",
          ),
      },
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
              <Youtube className="h-6 w-6 text-red-600" />
            </div>
          </div>
          <CardTitle>Select a YouTube Channel</CardTitle>
          <CardDescription>
            Choose which YouTube channel you want to connect to your profile.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-2">
          {channels.map((channel) => {
            const isSelected = channel.platformAccountId === selectedId;
            return (
              <button
                key={channel.platformAccountId}
                type="button"
                onClick={() => setSelectedId(channel.platformAccountId)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-all duration-150 ${
                  isSelected
                    ? "border-primary bg-primary/5 ring-1 ring-primary"
                    : "border-border hover:border-muted-foreground/40 hover:bg-muted/50"
                }`}
              >
                <Avatar className="h-10 w-10 shrink-0">
                  <AvatarImage
                    src={channel.avatarUrl}
                    alt={channel.displayName}
                  />
                  <AvatarFallback className="bg-red-100 text-red-700 text-sm font-medium">
                    {channel.displayName.slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>

                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">
                    {channel.displayName}
                  </p>
                  {channel.handle && (
                    <p className="text-xs text-muted-foreground truncate">
                      @{channel.handle}
                    </p>
                  )}
                </div>

                {isSelected && (
                  <CheckCircle className="h-5 w-5 text-primary shrink-0" />
                )}
              </button>
            );
          })}
        </CardContent>

        <CardFooter className="flex flex-col gap-3">
          <Button
            className="w-full"
            onClick={handleConnect}
            disabled={!selectedId || connectMutation.isPending}
          >
            {connectMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Connecting...
              </>
            ) : (
              "Connect Channel"
            )}
          </Button>

          <p className="text-xs text-muted-foreground text-center">
            Only channels you own on this Google account are listed. To connect
            a Brand Account channel, sign in with that account's Google
            identity.
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
